import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Globe, Database, AlertTriangle } from "lucide-react";
import { CurrencyCode } from "@/utils/currencyConverter";
import FAQSection from "../../components/common/sections/FAQ";
import { getWordPressHostingPlans } from "@/services/dreamscapeApi";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/domain/wordPressHosting.png";
import SEO from "@/components/common/seo/seo";

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

export default function WordPressHosting() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [isDummyData, setIsDummyData] = useState(false);

    useEffect(() => {
        // Load WordPress hosting plans from API
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);
            
            try {
                // Try to fetch real data from Dreamscape API (sandomains.com)
                const result = await getWordPressHostingPlans({ limit: 50 });
                
                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    // If API returns no plans, fall back to dummy data
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                // If API call fails, fall back to dummy data
                console.error('Failed to load plans from API, using dummy data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load plans');
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        // Fallback dummy data function
        const loadDummyPlans = () => {
            const dummyPlans = [
                {
                    id: 1,
                    name: "WordPress Starter",
                    description: "Perfect for small WordPress sites and blogs. Optimized for WordPress performance.",
                    periods: [
                        { period: 1, price: { register: 4.99, renew: 6.99 } },
                        { period: 12, price: { register: 3.99, renew: 5.99 } },
                        { period: 24, price: { register: 3.49, renew: 5.49 } },
                        { period: 36, price: { register: 2.99, renew: 4.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "10 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "WordPress Installations", value: "1" },
                        { name: "Email Accounts", value: "5" },
                        { name: "Auto WordPress Updates", value: "Yes" }
                    ]
                },
                {
                    id: 2,
                    name: "WordPress Business",
                    description: "Ideal for growing WordPress sites. More resources and WordPress-specific features.",
                    periods: [
                        { period: 1, price: { register: 8.99, renew: 12.99 } },
                        { period: 12, price: { register: 7.99, renew: 11.99 } },
                        { period: 24, price: { register: 6.99, renew: 10.99 } },
                        { period: 36, price: { register: 5.99, renew: 9.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "50 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "WordPress Installations", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "Auto WordPress Updates", value: "Yes" },
                        { name: "Daily Backups", value: "Yes" }
                    ]
                },
                {
                    id: 3,
                    name: "WordPress Professional",
                    description: "For professional WordPress sites and applications. Maximum performance and resources.",
                    periods: [
                        { period: 1, price: { register: 14.99, renew: 19.99 } },
                        { period: 12, price: { register: 12.99, renew: 17.99 } },
                        { period: 24, price: { register: 11.99, renew: 16.99 } },
                        { period: 36, price: { register: 10.99, renew: 15.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "Unlimited" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "WordPress Installations", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "Auto WordPress Updates", value: "Yes" },
                        { name: "Daily Backups", value: "Yes" },
                        { name: "Dedicated IP", value: "1" },
                        { name: "Staging Environment", value: "Yes" }
                    ]
                }
            ];
            setPlans(dummyPlans);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll('.hosting-section');

        sections.forEach((section, index) => {
            gsap.fromTo(section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected WordPress hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Zap,
            title: "Optimized Performance",
            description: "Lightning-fast WordPress hosting with optimized servers for maximum speed."
        },
        {
            icon: Shield,
            title: "Enhanced Security",
            description: "Advanced security features to protect your WordPress site from threats."
        },
        {
            icon: Server,
            title: "Auto Updates",
            description: "Automatic WordPress core, plugin, and theme updates for peace of mind."
        },
        {
            icon: Database,
            title: "Daily Backups",
            description: "Automated daily backups to ensure your data is always safe."
        },
        {
            icon: Globe,
            title: "Easy Migration",
            description: "Free WordPress site migration service to get you started quickly."
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert WordPress support available around the clock."
        }
    ];

    const wordpressHostingFAQs = [
        {
            question: "What Is WordPress Hosting?",
            answer: "WordPress is an open-source content management system that powers thousands of websites. Anyone can create and scale their website with WordPress thanks to the library of plugins and themes to choose from.<br /><br />WordPress Hosting, on the other hand, is a type of web hosting optimised for websites built on WordPress.<br /><br />Nearly all types of web hosting support WordPress sites. But what makes WordPress Hosting a better choice is that you get tools and features tailored for WordPress sites – including automatic software updates, security updates, and quick WordPress installation."
        },
        {
            question: "Can I upgrade anytime?",
            answer: "Yes. It's best to go with a plan that fits your business needs. Our web hosting plans allow you to upgrade anytime you need more resources. You can contact us to discuss the best option for your business."
        },
        {
            question: "How many customers share a server?",
            answer: "Our servers hold up to 2000 websites. Sharing the server with other clients allows you to use its resources at an affordable cost while maintaining optimal performance.<br /><br />On top of that, we do not overload our servers.<br /><br />Despite its power, servers can only hold up a certain limit. Limiting our servers' hard drive and memory usage at a maximum capacity of 80% ensures that all the websites can handle traffic spikes without compromising your application's performance."
        },
        {
            question: "How do I manage files in my WordPress account?",
            answer: "WordPress Hosting does not use cPanel or Plesk Hosting Manager. So, you'll be able to manage files directly on your WordPress Dashboard or using File Transfer Protocol (FTP) client.<br /><br />On your browser, type in your domain followed by /wp-admin to access your WordPress Dashboard login page. Logging on to your Account Manager and heading to Web Hosting will also lead you to your dashboard.<br /><br />Alternatively, you can use an FTP Client – an application you use to transfer files between your webserver and computer – to quickly upload/download files to/from your hosting account.<br /><br />You can use FileZilla, but any FTP client will do."
        },
        {
            question: "Do you offer help in creating a WordPress Website?",
            answer: "Our customer service team can assist you in the initial setup. Once ready, they may extend help with the basics – like dashboard navigation and plugin installation.<br /><br />If you want us to build a website with WordPress, check out our WordPress Design service."
        },
        {
            question: "Will you back up my website?",
            answer: "Our system automatically keeps a backup of your website. However, we highly encourage you to back up your files as well.<br /><br />You can create weekly or daily backups – depending on your preference. Doing so assures that your backup files are recent.<br /><br />Automate your backups if you cannot do it manually."
        },
        {
            question: "Tips When Updating Your Website to WordPress Hosting",
            answer: "After pointing the A record to WordPress Hosting, we recommend setting up a Maintenance Mode Page. On this page, users will know that the website is temporarily down. You can also add your contact details so they can lodge their inquiries.<br /><br />You can easily set up a Maintenance page with a plugin – just double check if the plugin you're using is safe and updated, of course!<br /><br />On the other hand, you can also setup a staging site. Staging sites are an excellent choice for eCommerce sites to limit revenue losses.<br /><br />Staging sites are \"duplicates\" of your live website. It allows your website to work without interruption while you update your site. Once the update goes well on staging, apply the changes and publish it on your live site."
        }
    ];

    return (
        <>
            <SEO
                title="Wordpress Hosting - SAN micron"
                description="Discover powerful and flexible WordPress hosting solutions with SAN micron. Perfect for businesses seeking reliable performance, robust security, and seamless integration with WordPress technologies."
            />
            <div className="relative overflow-hidden min-h-screen text-white">
                <Breadcrumbs
                    title="WordPress Hosting"
                    subtitle=""
                    breadcrumbText="WordPress Hosting"
                    backgroundImage={BreadcrumbsImage}
                />

                {/* Hero Section */}
                <ParallaxSection speed={0.25}>
                    <Floating3D intensity={18}>
                        <section className="hosting-section container py-20 pt-32">
                            <div className="text-center max-w-4xl mx-auto">
                                {/* <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                    WordPress Hosting
                                </h1> */}
                                <p className="text-xl text-textColor mb-8 leading-relaxed">
                                    Specialized hosting servers ideally built with WordPress websites in mind.
                                    Install amazing WordPress features and performance.
                                </p>
                            </div>
                        </section>
                    </Floating3D>
                </ParallaxSection>

                {/* Pricing Plans */}
                <AnimatedSection delay={0.2} direction="up">
                    <div className="hosting-section">
                        {isDummyData ? (
                            <div className="container py-12">
                                <div className="max-w-2xl mx-auto">
                                    <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-lg p-6 flex items-start gap-4">
                                        <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h3 className="text-yellow-400 font-bold text-lg mb-2">
                                                API Returns Dummy Data
                                            </h3>
                                            <p className="text-yellow-300/90 text-sm leading-relaxed">
                                                The API is currently returning dummy/placeholder data. Please check the API configuration or contact support to ensure real hosting plan data is available.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ProductPlansDisplay
                                plans={plans}
                                loading={loading}
                                error={error}
                                currency={selectedCurrency}
                                onCurrencyChange={setSelectedCurrency}
                                onSelectPlan={handleSelectPlan}
                                buttonText="Get Started"
                                priceLabel="per month"
                            />
                        )}
                    </div>
                </AnimatedSection>

                {/* Features Section */}
                <ParallaxSection speed={0.3} direction="down">
                    <AnimatedSection delay={0.3} direction="left">
                        <section className="hosting-section container py-20">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold mb-4">Why Choose WordPress Hosting?</h2>
                                <p className="text-textColor text-lg">Everything you need for a powerful WordPress website</p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                            <p className="text-textColor">{feature.description}</p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    </AnimatedSection>
                </ParallaxSection>

                {/* FAQ Section */}
                <AnimatedSection delay={0.6}>
                    <div className="abstract-bg hosting-section">
                        <FAQSection faqs={wordpressHostingFAQs} />
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.7}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}

